import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import React from 'react';
import { C, soft } from '@parsina/framework/clinical/src/constants/theme';
import { bffMutation } from '@parsina/framework/clinical/src/lib/bffClient';
import { useTranscription } from '@parsina/framework/clinical/src/hooks/useTranscription';

const UPDATE_VISIT = `
  mutation UpdateVisit($id: ID!, $input: VisitInput!) {
    updateVisit(id: $id, input: $input) { id status }
  }
`;

// Note: CREATE_CLINICAL_NOTE uses the new ClinicalNoteInput shape (content: JSON,
// noteType, authorRole) — avoids the double-encoding bug of the old addClinicalNote.
// appointmentId is required on ClinicalNoteInput; visitId is the best proxy here
// since VoiceRecord doesn't have a separate pmEncounterId wired up.
const CREATE_CLINICAL_NOTE = `mutation CreateClinicalNote($patientId:ID!,$input:ClinicalNoteInput!){createClinicalNote(patientId:$patientId,input:$input){id}}`;

// ── SVG icons ────────────────────────────────────────────────────────────────
const IcMic = ({ size = 28, color = 'currentColor' }) => (
  <svg viewBox="0 0 24 24" fill={color} width={size} height={size}>
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
  </svg>
);
const IcCheck = ({ size = 26, color = 'currentColor' }) => (
  <svg viewBox="0 0 24 24" fill={color} width={size} height={size}>
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);
const IcBack = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const IcBrain = ({ color = 'currentColor' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" width="14" height="14">
    <path d="M9 3a3 3 0 0 0-3 3v.17A3 3 0 0 0 4 9a3 3 0 0 0 2 2.83V14a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3v-2.17A3 3 0 0 0 20 9a3 3 0 0 0-2-2.83V6a3 3 0 0 0-3-3"/>
    <path d="M12 3v18"/>
  </svg>
);

// ── Keyframe injector ────────────────────────────────────────────────────────
const KEYFRAMES = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes vPulse {
    0%,100% { opacity: 0.3; transform: scale(1); }
    50%      { opacity: 0.7; transform: scale(1.1); }
  }
  @keyframes vWave0 { from { transform: scaleY(0.40); } to { transform: scaleY(1.00); } }
  @keyframes vWave1 { from { transform: scaleY(0.55); } to { transform: scaleY(1.00); } }
  @keyframes vWave2 { from { transform: scaleY(0.30); } to { transform: scaleY(0.95); } }
  @keyframes vWave3 { from { transform: scaleY(0.60); } to { transform: scaleY(1.00); } }
  @keyframes vWave4 { from { transform: scaleY(0.45); } to { transform: scaleY(0.85); } }
`;

// ── Waveform bars ────────────────────────────────────────────────────────────
const HEIGHTS = [30,55,70,45,80,60,90,50,75,40,85,55,65,40,70,50,80,45,60,35];
const WaveBar = ({ h, i, active }) => (
  <div style={{
    width: 2.5, borderRadius: 2, background: 'white',
    opacity: active ? 0.85 : 0.25,
    height: `${h}%`,
    transformOrigin: 'bottom center',
    transform: active ? 'none' : 'scaleY(0.3)',
    transition: `transform ${0.25 + i * 0.04}s ease, opacity 0.3s ease`,
    animation: active ? `vWave${i % 5} ${0.5 + (i % 4) * 0.15}s ease-in-out infinite alternate` : 'none',
    flexShrink: 0,
  }} />
);

const VoiceRecordMobile = ({ navigate, patient }) => {
  const { t, formatList } = useTranslation();
  const p = patient || { name: t('patient.sampleName'), id: 'v1' };
  const visitId = p.visitId || p.id || 'v1';
  const patientHistory = formatList([p.conditions, p.allergies].flat().filter(Boolean));

  const { recState, elapsed, hints, report: hookReport, startRec, stopRec } =
    useTranscription({ visitId, patientHistory });

  const [editMode, setEditMode] = React.useState(false);
  const [saving, setSaving]     = React.useState(false);
  const [report, setReport]     = React.useState({ symptoms:'', diagnosis:'', notes:'' });

  React.useEffect(() => { if (hookReport) setReport(hookReport); }, [hookReport]);

  const fmt = s =>
    `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const handleSave = () => {
    setSaving(true);
    // UPDATE_VISIT no longer accepts diagnosis/notes — those fields were removed from VisitInput.
    // Persist only status; save the voice report as a SOAP-shaped clinical note.
    const patientId = p.patientId || p.id;
    Promise.all([
      bffMutation(UPDATE_VISIT, { id: visitId, input: { status: 'inprogress' } }),
      patientId
        ? bffMutation(CREATE_CLINICAL_NOTE, {
            patientId,
            input: {
              noteType: 'SOAP_NOTE',
              content: {
                s: report.symptoms || '',
                o: '',
                a: report.diagnosis || '',
                p: report.notes || '',
              },
              authorRole: 'doctor',
              appointmentId: visitId,
            },
          })
        : Promise.resolve(),
    ])
      .then(() => setEditMode(false))
      .finally(() => setSaving(false));
  };

  const stateLabel = {
    idle: t('visit.record.ready'),
    recording: t('visit.record.recording'),
    stopping: t('visit.record.processingAudio'),
    processing: t('visit.record.analysing'),
    done: t('visit.record.done'),
  }[recState] || t('visit.record.ready');

  const panelBg = (recState === 'processing' || recState === 'stopping')
    ? `color-mix(in srgb, ${C.teal} 12%, var(--panel-ink-deep))`
    : recState === 'done'
    ? '#071e14'
    : 'var(--panel-ink-deep)';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#ffffff', overflowY: 'auto' }}>
      <style>{KEYFRAMES}</style>

      {/* ── Header ── */}
      <div style={{
        background: 'var(--panel-ink-deep)',
        padding: '14px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('visit', p)} style={{
            background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 10,
            width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'white',
          }}>
            <IcBack />
          </button>
          <div>
            <div style={{ color: 'white', fontSize: '0.9375rem', fontWeight: 600 }}>{t('nav.items.voice')}</div>
            <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.6875rem', marginTop: 1 }}>{p.name}</div>
          </div>
        </div>
        {recState === 'done' && (
          <button onClick={() => navigate('prescription', { ...p, visitId, id: p.id })} style={{
            background: C.navy, border: 'none', borderRadius: 10, padding: '8px 16px',
            fontSize: '0.75rem', fontWeight: 600, color: 'white', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {t('nav.items.prescription')}
            <svg viewBox="0 0 24 24" fill="white" width="13" height="13"><path d="M8 5v14l11-7z"/></svg>
          </button>
        )}
      </div>

      {/* ── Recording panel ── */}
      <div style={{
        background: panelBg,
        padding: '36px 24px 32px',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        transition: 'background 0.6s ease',
        flexShrink: 0,
      }}>

        {/* State label */}
        <div style={{
          fontSize: '0.625rem', letterSpacing: '1.4px', fontWeight: 700,
          textTransform: 'uppercase',
          color: recState === 'recording' ? C.teal
               : recState === 'done'      ? '#4ade80'
               : 'rgba(255,255,255,0.35)',
          marginBottom: 24,
        }}>
          {stateLabel}
        </div>

        {/* Timer (recording only) */}
        {recState === 'recording' && (
          <div style={{
            fontSize: '2.75rem', fontWeight: 200, color: 'white',
            letterSpacing: 6, fontVariantNumeric: 'tabular-nums',
            marginBottom: 28, lineHeight: 1,
          }}>
            {fmt(elapsed)}
          </div>
        )}

        {/* Waveform / spinner / done */}
        <div style={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 28 }}>
          {recState === 'processing' ? (
            <div style={{
              width: 36, height: 36, border: '2px solid rgba(255,255,255,0.15)',
              borderTopColor: C.teal, borderRadius: '50%',
              animation: 'spin 0.9s linear infinite',
            }} />
          ) : recState === 'done' ? (
            <div style={{
              width: 52, height: 52, borderRadius: '50%',
              background: 'rgba(74,222,128,0.15)',
              border: '1.5px solid rgba(74,222,128,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <IcCheck size={24} color="#4ade80" />
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {HEIGHTS.map((h, i) => (
                <WaveBar key={i} h={h} i={i} active={recState === 'recording'} />
              ))}
            </div>
          )}
        </div>

        {/* Action button */}
        {recState === 'idle' && (
          <button onClick={startRec} style={{
            width: 76, height: 76, borderRadius: '50%',
            background: 'transparent',
            border: '1.5px solid rgba(255,255,255,0.25)',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative',
            transition: 'border-color 0.2s',
          }}
            onMouseDown={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)'; e.currentTarget.style.transform = 'scale(0.96)'; }}
            onMouseUp={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <div style={{
              position: 'absolute', inset: -8, borderRadius: '50%',
              border: '1px solid rgba(255,255,255,0.08)',
              animation: 'vPulse 2.4s ease-in-out infinite',
            }} />
            <IcMic size={30} color="white" />
          </button>
        )}
        {recState === 'recording' && (
          <button onClick={stopRec} style={{
            width: 64, height: 64, borderRadius: '50%',
            background: C.red, border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 0 0 6px rgba(217,48,37,0.18)`,
          }}>
            <div style={{ width: 20, height: 20, background: 'white', borderRadius: 3 }} />
          </button>
        )}
        {recState === 'processing' && (
          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.5px', marginTop: 4 }}>
            {t('visit.record.transcribing')}
          </div>
        )}
      </div>

      {/* ── Clinical hints list ── */}
      {hints.length > 0 && (
        <div style={{ margin: '16px 16px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
            {recState === 'recording' && (
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.teal, animation: 'vPulse 1.2s infinite', flexShrink: 0 }} />
            )}
            <IcBrain color={C.teal} />
            <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: C.teal, letterSpacing: '0.6px', textTransform: 'uppercase' }}>
              {t('visit.suggest.clinical')}
            </span>
            <span style={{ marginInlineStart: 'auto', fontSize: '0.6875rem', color: C.textMuted, fontWeight: 600 }}>
              {hints.length} {t('visit.suggest.one')}
            </span>
          </div>
          <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {hints.map((h, i) => (
              <div key={i} style={{
                background: 'white',
                border: `1px solid ${C.border}`,
                borderInlineStart: `3px solid ${i === 0 ? C.teal : '#e2e8f0'}`,
                borderRadius: 12,
                padding: '12px 14px',
              }}>
                <div style={{ fontSize: '0.625rem', fontWeight: 700, color: i === 0 ? C.teal : C.textMuted, letterSpacing: '0.5px', marginBottom: 6 }}>
                  {i === 0 ? t('visit.suggest.latest') : t('visit.suggest.numbered', { n: hints.length - i })}
                </div>
                <div style={{ fontSize: '0.8125rem', color: C.textPrimary, lineHeight: 1.75 }}>{typeof h === 'string' ? h : h.text || ''}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Report ── */}
      {recState === 'done' && (
        <div style={{ padding: '20px 16px 100px' }}>

          {/* Section header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: C.textPrimary }}>{t('visit.report.title')}</div>
              <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginTop: 2 }}>{t('visit.report.aiGenerated')}</div>
            </div>
            <button onClick={() => editMode ? handleSave() : setEditMode(true)} style={{
              padding: '7px 16px', borderRadius: 20,
              border: `1.5px solid ${editMode ? C.navy : C.border}`,
              background: editMode ? C.navy : 'white',
              color: editMode ? 'white' : C.textSecondary,
              fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              transition: 'all 0.2s',
            }}>
              {editMode ? (
                <><IcCheck size={14} color="white" /> {t('common.actions.save')}</>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke={C.textSecondary} strokeWidth="2" width="13" height="13">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  {t('common.actions.edit')}
                </>
              )}
            </button>
          </div>

          {/* Main report fields */}
          <div style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden', marginBottom: 12 }}>
            {[
              {
                key: 'symptoms', label: t('visit.report.historyAndSymptoms'),
                icon: <svg viewBox="0 0 24 24" fill="none" stroke={C.textMuted} strokeWidth="1.8" width="14" height="14"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
              },
              {
                key: 'diagnosis', label: t('visit.report.probableDiagnosis'),
                icon: <svg viewBox="0 0 24 24" fill="none" stroke={C.textMuted} strokeWidth="1.8" width="14" height="14"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
              },
              {
                key: 'notes', label: t('patient.overview.clinicalSummary'),
                icon: <svg viewBox="0 0 24 24" fill="none" stroke={C.textMuted} strokeWidth="1.8" width="14" height="14"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
              },
            ].map((f, idx, arr) => (
              <div key={f.key} style={{
                padding: '16px',
                borderBottom: idx < arr.length - 1 ? `1px solid ${C.border}` : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  {f.icon}
                  <span style={{ fontSize: '0.625rem', letterSpacing: '0.8px', fontWeight: 700, color: C.textMuted, textTransform: 'uppercase' }}>{f.label}</span>
                </div>
                {editMode ? (
                  <textarea
                    value={report[f.key]}
                    onChange={e => setReport(r => ({ ...r, [f.key]: e.target.value }))}
                    style={{
                      width: '100%', minHeight: 70, border: `1.5px solid ${C.border}`,
                      borderRadius: 8, padding: '10px 12px', fontSize: '0.8125rem',
                      fontFamily: 'var(--app-font)', resize: 'none', outline: 'none',
                      background: '#fafbfc', color: C.textPrimary, lineHeight: 1.7,
                    }}
                  />
                ) : (
                  <div style={{ fontSize: '0.8125rem', color: C.textPrimary, lineHeight: 1.75 }}>
                    {report[f.key] || <span style={{ color: C.textMuted }}>—</span>}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ICD codes */}
          {report.icdCodes?.length > 0 && (
            <div style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke={C.textMuted} strokeWidth="1.8" width="14" height="14">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
                </svg>
                <span style={{ fontSize: '0.625rem', letterSpacing: '0.8px', fontWeight: 700, color: C.textMuted, textTransform: 'uppercase' }}>{t('visit.report.icd10')}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {report.icdCodes.map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{
                      fontFamily: 'monospace', fontSize: '0.6875rem', fontWeight: 700,
                      color: C.navy, background: soft(C.navy, 0.07),
                      padding: '3px 8px', borderRadius: 6, flexShrink: 0,
                      letterSpacing: '0.5px',
                    }}>{c.code}</span>
                    <span style={{ fontSize: '0.75rem', color: C.textSecondary }}>{c.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suggested actions */}
          {report.suggestedActions?.length > 0 && (
            <div style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke={C.textMuted} strokeWidth="1.8" width="14" height="14">
                  <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                </svg>
                <span style={{ fontSize: '0.625rem', letterSpacing: '0.8px', fontWeight: 700, color: C.textMuted, textTransform: 'uppercase' }}>{t('patient.upload.suggestedActions')}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {report.suggestedActions.map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${C.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1,
                    }}>
                      <span style={{ fontSize: '0.625rem', color: C.textMuted }}>{i + 1}</span>
                    </div>
                    <span style={{ fontSize: '0.8125rem', color: C.textPrimary, lineHeight: 1.65 }}>{a}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suggested medications */}
          {report.suggestedMedications?.length > 0 && (
            <div style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke={C.textMuted} strokeWidth="1.8" width="14" height="14">
                  <path d="M10.5 20H4a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2h3.93a2 2 0 0 1 1.66.9l.82 1.2a2 2 0 0 0 1.66.9H20a2 2 0 0 1 2 2v3"/><circle cx="18" cy="18" r="3"/><path d="M20.5 15.5 18 18l-1.5-1.5"/>
                </svg>
                <span style={{ fontSize: '0.625rem', letterSpacing: '0.8px', fontWeight: 700, color: C.textMuted, textTransform: 'uppercase' }}>{t('visit.rx.suggested')}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                {report.suggestedMedications.map((m, i) => (
                  <span key={i} style={{
                    fontSize: '0.75rem', color: C.navy, fontWeight: 500,
                    background: soft(C.navy, 0.06), border: `1px solid ${soft(C.navy, 0.18)}`,
                    borderRadius: 20, padding: '5px 12px',
                  }}>{m}</span>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button onClick={() => navigate('prescription', { ...p, visitId, id: p.id })} style={{
              flex: 3, height: 52,
              background: C.navy,
              color: 'white', border: 'none', borderRadius: 14,
              fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              <svg viewBox="0 0 24 24" fill="white" width="16" height="16"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 9h6m-6 4h4"/></svg>
              {t('nav.items.prescription')}
            </button>
            <button onClick={handleSave} disabled={saving} style={{
              flex: 1, height: 52,
              background: 'white', color: saving ? C.textMuted : C.textSecondary,
              border: `1.5px solid ${C.border}`, borderRadius: 14,
              fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer',
            }}>
              {saving ? '...' : t('common.actions.save')}
            </button>
          </div>
        </div>
      )}

      {recState !== 'done' && <div style={{ height: 32 }} />}
    </div>
  );
};

export default VoiceRecordMobile;
